import { ajv } from '@cibo/core'

function resolveJsonPointer(root: any, refPath: string): any {
  if (!refPath.startsWith('#/')) return undefined

  const segments = refPath
    .slice(2)
    .split('/')
    .map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'))

  return segments.reduce((current, segment) => {
    if (!current || typeof current !== 'object') return undefined
    return current[segment]
  }, root)
}

/**
 * Resolves JSON Schema $ref references (e.g., "#/$defs/cropCompletion") by looking up
 * the definition in the root schema. If the schema node contains a $ref, this function
 * follows the JSON Pointer path to retrieve the actual schema definition. Returns the
 * resolved schema or the original node if no $ref exists or resolution fails.
 */
function resolveRef(schemaNode: any, rootSchema: any): any {
  if (!schemaNode || typeof schemaNode !== 'object') return schemaNode
  if (!schemaNode.$ref || typeof schemaNode.$ref !== 'string') return schemaNode

  return resolveJsonPointer(rootSchema, schemaNode.$ref) || schemaNode
}

function evaluateCondition(condition: any, model: any, rootSchema: any): boolean {
  if (!condition) return false

  try {
    const resolvedCondition = resolveRef(condition, rootSchema)

    const tempSchema: any = {
      type: 'object',
      ...resolvedCondition,
    }

    if (rootSchema?.$defs) {
      tempSchema.$defs = rootSchema.$defs
    }

    if (rootSchema?.definitions) {
      tempSchema.definitions = rootSchema.definitions
    }

    const validator = ajv.compile(tempSchema)
    return !!validator(model)
  } catch {
    return false
  }
}

function markRequired(requiredLookup: Map<string, boolean>, basePath: string, required: string[]) {
  if (!required || !Array.isArray(required)) return

  required.forEach(field => {
    const fullPath = basePath ? `${basePath}.${field}` : field
    requiredLookup.set(fullPath, true)
  })
}

/**
 * Stock Uniforms only marks fields as required if they are listed in the "required" array of the
 * current schema node.
 *
 * Since we modify what is required according to model conditions, we need to compute the full list
 * of required fields by traversing the schema and evaluating conditions. This function recursively
 * walks through the schema, handling "required", "if/then/else", "allOf", "anyOf", "oneOf", and
 * dependencies to build a comprehensive lookup of which fields are required based on the current
 * model state.
 */
// visual complexity is 22 :/
// eslint-disable-next-line visual/complexity
export function collectRequiredFields(
  schemaNode: any,
  modelNode: any,
  basePath: string,
  requiredLookup: Map<string, boolean>,
  rootSchema: any
) {
  if (!schemaNode || typeof schemaNode !== 'object') return

  const activeRootSchema = schemaNode.$defs || schemaNode.definitions ? schemaNode : rootSchema

  if (schemaNode.required) {
    markRequired(requiredLookup, basePath, schemaNode.required)
  }

  if (schemaNode.if) {
    const conditionMet = evaluateCondition(schemaNode.if, modelNode, activeRootSchema)

    if (conditionMet && schemaNode.then) {
      const resolvedThen = resolveRef(schemaNode.then, activeRootSchema)
      collectRequiredFields(resolvedThen, modelNode, basePath, requiredLookup, activeRootSchema)
    } else if (!conditionMet && schemaNode.else) {
      const resolvedElse = resolveRef(schemaNode.else, activeRootSchema)
      collectRequiredFields(resolvedElse, modelNode, basePath, requiredLookup, activeRootSchema)
    }
  }

  if (Array.isArray(schemaNode.allOf)) {
    schemaNode.allOf.forEach((subSchema: any) => {
      const resolved = resolveRef(subSchema, activeRootSchema)
      collectRequiredFields(resolved, modelNode, basePath, requiredLookup, activeRootSchema)
    })
  }

  if (Array.isArray(schemaNode.anyOf)) {
    schemaNode.anyOf.forEach((subSchema: any) => {
      const resolved = resolveRef(subSchema, activeRootSchema)
      collectRequiredFields(resolved, modelNode, basePath, requiredLookup, activeRootSchema)
    })
  }

  if (Array.isArray(schemaNode.oneOf)) {
    schemaNode.oneOf.forEach((subSchema: any) => {
      const resolved = resolveRef(subSchema, activeRootSchema)
      collectRequiredFields(resolved, modelNode, basePath, requiredLookup, activeRootSchema)
    })
  }

  if (schemaNode.dependentRequired && typeof schemaNode.dependentRequired === 'object') {
    Object.entries(schemaNode.dependentRequired).forEach(([dependentField, requiredFields]) => {
      if (
        modelNode &&
        modelNode[dependentField] !== undefined &&
        modelNode[dependentField] !== null
      ) {
        markRequired(requiredLookup, basePath, requiredFields as string[])
      }
    })
  }

  if (schemaNode.dependencies && typeof schemaNode.dependencies === 'object') {
    Object.entries(schemaNode.dependencies).forEach(([dependentField, dep]) => {
      if (
        modelNode &&
        modelNode[dependentField] !== undefined &&
        modelNode[dependentField] !== null
      ) {
        if (Array.isArray(dep)) {
          markRequired(requiredLookup, basePath, dep)
        } else if (typeof dep === 'object') {
          const resolved = resolveRef(dep, activeRootSchema)
          collectRequiredFields(resolved, modelNode, basePath, requiredLookup, activeRootSchema)
        }
      }
    })
  }

  if (schemaNode.properties && typeof schemaNode.properties === 'object') {
    Object.entries(schemaNode.properties).forEach(([propName, propSchema]) => {
      const propPath = basePath ? `${basePath}.${propName}` : propName
      const propModel = modelNode?.[propName]
      const resolvedPropSchema = resolveRef(propSchema, activeRootSchema)

      collectRequiredFields(
        resolvedPropSchema,
        propModel,
        propPath,
        requiredLookup,
        activeRootSchema
      )
    })
  }

  // Handle array items
  if (schemaNode.items && Array.isArray(modelNode)) {
    const resolvedItemSchema = resolveRef(schemaNode.items, activeRootSchema)
    modelNode.forEach((itemModel, index) => {
      const itemPath = basePath ? `${basePath}.${index}` : String(index)
      collectRequiredFields(
        resolvedItemSchema,
        itemModel,
        itemPath,
        requiredLookup,
        activeRootSchema
      )
    })
  }
}

export function buildRequiredFieldsLookup(schema: any, model: any): Map<string, boolean> {
  const requiredLookup = new Map<string, boolean>()

  if (!schema || typeof schema !== 'object') return requiredLookup

  collectRequiredFields(schema, model, '', requiredLookup, schema)
  return requiredLookup
}
